import { Component, Suspense, useEffect } from "react";
import type { ErrorInfo, ReactNode } from "react";

import * as Sentry from "@sentry/react";

// @mui material components
import CircularProgress from "@mui/material/CircularProgress";

// Vision UI Dashboard PRO React components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";

type ChildrenProps = {
  children?: ReactNode;
};

type ChunkErrorBoundaryState = {
  failed: boolean;
};

const Centered = ({ children }: ChildrenProps) => (
  <VuiBox
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap={2}
    minHeight="60vh"
  >
    {children}
  </VuiBox>
);

const Fallback = (
  <Centered>
    <CircularProgress color="info" />
  </Centered>
);

const RELOAD_FLAG = "chunk-reload";

// A tab left open across a deploy asks for a chunk hash that no longer exists. One
// silent reload fixes that; the flag stops a broken build from looping on it.
class ChunkErrorBoundary extends Component<ChildrenProps, ChunkErrorBoundaryState> {
  constructor(props: ChildrenProps) {
    super(props);

    this.state = { failed: false };
  }

  static getDerivedStateFromError(): ChunkErrorBoundaryState {
    return { failed: true };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A boundary swallows the error, so sentry's global handler never sees it.
    Sentry.captureException(error, { extra: { ...errorInfo } });

    const isChunkError =
      error?.name === "ChunkLoadError" || /Loading chunk \S+ failed/i.test(error?.message || "");

    if (!isChunkError) {
      return;
    }

    let alreadyReloaded = false;

    try {
      alreadyReloaded = window.sessionStorage.getItem(RELOAD_FLAG) === "1";
      window.sessionStorage.setItem(RELOAD_FLAG, "1");
    } catch (storageError) {
      // Private mode. Without the flag a reload could loop, so do not attempt one.
      alreadyReloaded = true;
    }

    if (!alreadyReloaded) {
      window.location.reload();
    }
  }

  override render() {
    if (this.state.failed) {
      return (
        <Centered>
          <VuiTypography variant="button" color="white">
            This page could not be loaded.
          </VuiTypography>
          <VuiButton color="info" onClick={() => window.location.reload()}>
            Reload
          </VuiButton>
        </Centered>
      );
    }

    return this.props.children;
  }
}

// Mounts only once the lazy sibling has resolved, so the flag is cleared on success.
function ClearReloadFlag() {
  useEffect(() => {
    try {
      window.sessionStorage.removeItem(RELOAD_FLAG);
    } catch (storageError) {
      /* nothing to clear when storage is unavailable */
    }
  }, []);

  return null;
}

function RouteSuspense({ children }: ChildrenProps) {
  return (
    <ChunkErrorBoundary>
      <Suspense fallback={Fallback}>
        <ClearReloadFlag />
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export default RouteSuspense;
